import {
  Avatar,
  Container,
  Flex,
  Grid,
  Heading,
  Text,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  HStack,
  Icon,
  Spinner,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Video } from "lucide-react";
import Layout from "../../../components/student/Layout/layout";
import VideoCard from "../../../containers/student/video/video";
import { getSectionRequest } from "../../../modules/students-modules/sections/Actions";
import { getVideosByChapterIdRequest, getVideosByChapterIdSuccess } from "../../../modules/students-modules/videos/Actions";
import axiosInstance from "../../../helpers/api";

const VideosChaptersList = () => {
  const [getSearchParams] = useSearchParams();
  const section_name = getSearchParams.get("name");
  const navigate = useNavigate();
  const { id } = useParams();
  const section = useSelector((state) => state.sections.section);
  const isLoading = useSelector((state) => state.sections.isLoading);
  const baseUrl = "https://asquera.com/asquera";
  const [expandedChapters, setExpandedChapters] = useState([]);
  const [allVideos, setAllVideos] = useState({});
  const [loadingChapters, setLoadingChapters] = useState(new Set());

  const dispatch = useDispatch();
  
  useEffect(() => {
    if (id) {
      // Clear previous data when id changes
      setAllVideos({});
      setExpandedChapters([]);
      setLoadingChapters(new Set());
      dispatch(getSectionRequest(id));
    }
  }, [id, dispatch]);

  // Remove prefetching - videos will be loaded on demand when chapters are clicked

  const handleChapterClick = async (chapterId) => {
    if (expandedChapters.includes(chapterId)) {
      // Close the chapter
      setExpandedChapters(prev => prev.filter(id => id !== chapterId));
    } else {
      // Open the chapter
      setExpandedChapters(prev => [...prev, chapterId]);
      
             // Check if videos are already loaded for this chapter
       if (!allVideos[chapterId]) {
         try {
           // Set loading state for this specific chapter
           setLoadingChapters(prev => new Set(prev).add(chapterId));
           
           // Fetch videos for this specific chapter
           const response = await axiosInstance.get(`/students/videos/${chapterId}`);
           const videos = response.data.data || [];
           
           // Update videos map with the fetched videos
           setAllVideos(prev => ({ ...prev, [chapterId]: videos }));
           
           // Update Redux state with videos for this chapter
           dispatch(getVideosByChapterIdSuccess({data: videos}));
         } catch (error) {
           console.error(`Error fetching videos for chapter ${chapterId}:`, error);
           // Set empty array for failed requests
           setAllVideos(prev => ({ ...prev, [chapterId]: [] }));
           dispatch(getVideosByChapterIdSuccess({data: []}));
         } finally {
           setLoadingChapters(prev => {
             const newSet = new Set(prev);
             newSet.delete(chapterId);
             return newSet;
           });
         }
       } else {
         // Videos already loaded, just update Redux state
         const chapterVideos = allVideos[chapterId] || [];
         dispatch(getVideosByChapterIdSuccess({data: chapterVideos}));
       }
    }
  };

  const handleVideoClick = (videoData, chapterName) => {
    navigate(
      `/video/${videoData?.id}?name=${chapterName}&video=${videoData?.id}`
    );
  };

  const renderVideoContent = (chapterId) => {
    const isExpanded = expandedChapters.includes(chapterId);
    const chapterVideos = allVideos[chapterId] || [];
    const isLoadingChapter = loadingChapters.has(chapterId);

    if (!isExpanded) return null;

    if (isLoadingChapter) {
      return (
        <VStack spacing="3" w="100%" align="stretch" p={4}>
          {[1, 2, 3].map((i) => (
            <Box key={i} p={4} borderWidth="1px" borderRadius="md">
              <Skeleton height="20px" mb={2} />
              <SkeletonText noOfLines={2} spacing="2" />
            </Box>
          ))}
        </VStack>
      );
    }

    if (chapterVideos.length > 0) {
      return (
        <VStack spacing="1" w="100%" align="stretch">
          {chapterVideos.map((video, idx) => (
            <Box
              key={idx}
              mx={2}
              borderRadius="md"
              transition="all 0.2s"
              _hover={{ bg: "gray.50" }}
            >
              <VideoCard
                data={video}
                index={idx}
                handelClick={() => handleVideoClick(video, section?.find(ch => ch.id === chapterId)?.name)}
              />
            </Box>
          ))}
        </VStack>
      );
    }

    return (
      <Box p={4} textAlign="center" color="gray.500">
        <Icon as={Video} boxSize={8} mb={2} />
        <Text>You Didn't pay for this chapter or this chapter has no videos</Text>
      </Box>
    );
  };

  return (
    <Layout color="main_1" bg="white" isLoading={isLoading}>
      <Container maxW="container.2xl" h="100%" my="100px" mb="165px" w="100%">
        <VStack spacing={8} h="100%" w="100%" alignItems="flex-start">
          <Heading as="h3" color="main_1" textAlign="center" w="100%">
            {section_name}
          </Heading>
          
          <Grid
            templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
            gap={6}
            w="100%"
          >
            {section?.map((chapter, index) => (
              <Accordion key={chapter?.id} allowMultiple>
                <AccordionItem border="none">
                  <AccordionButton
                    bg="#ededed"
                    borderRadius="8px"
                    p="10px"
                    h="70px"
                    w="100%"
                    _hover={{ bg: "#e0e0e0" }}
                    onClick={() => handleChapterClick(chapter?.id)}
                    disabled={loadingChapters.has(chapter?.id)}
                    opacity={loadingChapters.has(chapter?.id) ? 0.6 : 1}
                  >
                    <HStack flex="1" justify="flex-start" gap="20px">
                      <Avatar
                        name={chapter?.name}
                        src={baseUrl + "/" + chapter?.logo}
                        borderRadius="8px"
                        size="md"
                        background="transparent"
                      />
                      <Text fontWeight="700" fontSize="20px">
                        {chapter?.name}
                      </Text>
                    </HStack>
                    {loadingChapters.has(chapter?.id) ? (
                      <Spinner size="sm" color="main_1" />
                    ) : (
                      <AccordionIcon />
                    )}
                  </AccordionButton>
                  
                  <AccordionPanel pb={4}>
                    {renderVideoContent(chapter?.id)}
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            ))}
          </Grid>
        </VStack>
      </Container>
    </Layout>
  );
};

export default VideosChaptersList;
